import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { Plus, Copy, Check, Ban, Code2, X } from 'lucide-react';
import { embedKeys as api } from '../api';
import { useAuth } from '../contexts/AuthContext';
import DataTable, { Column } from '../components/DataTable';
import type { EmbedKey, PendingSignup } from '../types';

export default function EmbedKeysPage() {
  const { t } = useTranslation();
  const qc = useQueryClient();
  const { user } = useAuth();
  const mayEdit = user?.role === 'ADMIN' || user?.role === 'PRODUCT_MANAGER';

  const [showCreate, setShowCreate] = useState(false);
  const [snippetFor, setSnippetFor] = useState<EmbedKey | null>(null);
  const [signupsFor, setSignupsFor] = useState<EmbedKey | null>(null);
  const [copied, setCopied] = useState('');
  const [error, setError] = useState('');

  const { data: keys, isLoading } = useQuery({ queryKey: ['embed-keys'], queryFn: api.list });

  const revokeMutation = useMutation({
    mutationFn: (id: string) => api.revoke(id),
    onSuccess: () => qc.invalidateQueries({ queryKey: ['embed-keys'] }),
    onError: () => setError('Widerrufen fehlgeschlagen.'),
  });

  function copy(text: string, id: string) {
    void navigator.clipboard.writeText(text);
    setCopied(id);
    setTimeout(() => setCopied(''), 1800);
  }

  const columns: Column<EmbedKey>[] = [
    {
      key: 'name',
      label: 'Website',
      render: (k) => (
        <div>
          <div className={`font-medium ${k.revokedAt ? 'text-slate-400 line-through' : 'text-slate-900'}`}>
            {k.name}
          </div>
          <div className="text-xs text-slate-500">{k.allowedOrigins.join(', ')}</div>
        </div>
      ),
    },
    {
      key: 'key',
      label: 'Embed-Key',
      render: (k) => (
        <button
          onClick={() => copy(k.key, k.id)}
          title={t('embed.copyToClipboard')}
          className="inline-flex items-center gap-1.5 text-xs font-mono bg-slate-100 hover:bg-slate-200 px-2 py-1 rounded"
        >
          {k.key.slice(0, 20)}…
          {copied === k.id ? <Check size={12} className="text-green-600" /> : <Copy size={12} />}
        </button>
      ),
    },
    {
      key: 'signups',
      label: 'Anfragen',
      render: (k) => (
        <button
          onClick={() => setSignupsFor(k)}
          className="text-xs text-brand-600 hover:text-brand-800 font-medium"
        >
          {k._count?.signups ?? 0} ansehen
        </button>
      ),
    },
    {
      key: 'lastUsedAt',
      label: 'Zuletzt genutzt',
      render: (k) => (
        <span className="text-xs text-slate-500">
          {k.lastUsedAt ? new Date(k.lastUsedAt).toLocaleString('de-DE') : 'nie'}
        </span>
      ),
    },
    {
      key: 'actions',
      label: 'Aktionen',
      render: (k) => (
        <div className="flex items-center gap-3">
          <button
            onClick={() => setSnippetFor(k)}
            className="inline-flex items-center gap-1 text-xs text-slate-600 hover:text-slate-900 font-medium"
          >
            <Code2 size={12} /> Einbindung
          </button>
          {mayEdit && !k.revokedAt && (
            <button
              onClick={() => {
                if (confirm(`Embed-Key für "${k.name}" widerrufen? Das Widget auf dieser Website hört sofort auf zu funktionieren.`)) {
                  revokeMutation.mutate(k.id);
                }
              }}
              className="inline-flex items-center gap-1 text-xs text-red-600 hover:text-red-800 font-medium"
            >
              <Ban size={12} /> Widerrufen
            </button>
          )}
          {k.revokedAt && <span className="text-xs text-slate-400">widerrufen</span>}
        </div>
      ),
    },
  ];

  return (
    <div className="space-y-4">
      <div className="flex items-start justify-between">
        <p className="text-sm text-slate-500">
          Ein Key je Marketing-Website. Der Key ist kein Geheimnis — er steht im Seitenquelltext —
          und funktioniert nur von den hier freigegebenen Domains.
        </p>
        {mayEdit && (
          <button
            onClick={() => { setShowCreate(true); setError(''); }}
            className="inline-flex items-center gap-1.5 bg-brand-600 hover:bg-brand-700 text-white text-sm font-medium px-3.5 py-2 rounded-lg shrink-0 ml-4"
          >
            <Plus size={15} /> Neuer Key
          </button>
        )}
      </div>

      {error && (
        <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>
      )}

      <DataTable
        columns={columns}
        data={keys ?? []}
        loading={isLoading}
        emptyMessage="Noch keine Embed-Keys angelegt."
        rowKey={(k) => k.id}
      />

      {showCreate && (
        <CreateKeyModal
          onClose={() => setShowCreate(false)}
          onCreated={(created) => {
            setShowCreate(false);
            qc.invalidateQueries({ queryKey: ['embed-keys'] });
            setSnippetFor(created);
          }}
        />
      )}
      {snippetFor && <SnippetModal embedKey={snippetFor} onClose={() => setSnippetFor(null)} />}
      {signupsFor && <SignupsModal embedKey={signupsFor} onClose={() => setSignupsFor(null)} />}
    </div>
  );
}

// ─── Create ──────────────────────────────────────────────────────────────────

function CreateKeyModal({
  onClose,
  onCreated,
}: {
  onClose: () => void;
  onCreated: (key: EmbedKey) => void;
}) {
  const { t } = useTranslation();
  const [name, setName] = useState('');
  const [origins, setOrigins] = useState('');
  const [error, setError] = useState('');

  const mutation = useMutation({
    mutationFn: () =>
      api.create({
        name,
        allowedOrigins: origins
          .split('\n')
          .map((o) => o.trim().replace(/\/$/, ''))
          .filter(Boolean),
      }),
    onSuccess: onCreated,
    onError: (err: unknown) => {
      const msg =
        (err as { response?: { data?: { message?: string } } })?.response?.data?.message ??
        'Anlegen fehlgeschlagen.';
      setError(msg);
    },
  });

  return (
    <Modal title={t('embed.newKey')} onClose={onClose}>
      <form
        onSubmit={(e) => {
          e.preventDefault();
          setError('');
          mutation.mutate();
        }}
        className="px-6 py-5 space-y-4"
      >
        {error && (
          <div className="bg-red-50 border border-red-200 text-red-700 px-3 py-2 rounded text-sm">{error}</div>
        )}
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">Bezeichnung</label>
          <input
            required
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder={t('embed.exampleName')}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-brand-500"
          />
        </div>
        <div>
          <label className="block text-sm font-medium text-slate-700 mb-1">
            Erlaubte Domains (eine pro Zeile)
          </label>
          <textarea
            required
            rows={4}
            value={origins}
            onChange={(e) => setOrigins(e.target.value)}
            placeholder={'https://www.juni.com\nhttps://ojfm.de'}
            className="w-full border border-slate-300 rounded-lg px-3 py-2 text-sm font-mono focus:outline-none focus:ring-2 focus:ring-brand-500 resize-y"
          />
          <p className="text-xs text-slate-500 mt-1">
            Nur Schema + Domain, ohne Pfad. Jede Subdomain und auch http/https zählen separat.
          </p>
        </div>
        <div className="flex justify-end gap-2 pt-2">
          <button type="button" onClick={onClose} className="px-4 py-2 text-sm border border-slate-300 rounded-lg text-slate-600">
            Abbrechen
          </button>
          <button
            type="submit"
            disabled={mutation.isPending}
            className="px-4 py-2 text-sm bg-brand-600 text-white rounded-lg disabled:opacity-60"
          >
            {mutation.isPending ? 'Wird angelegt…' : 'Anlegen'}
          </button>
        </div>
      </form>
    </Modal>
  );
}

// ─── Snippet ─────────────────────────────────────────────────────────────────

function SnippetModal({ embedKey, onClose }: { embedKey: EmbedKey; onClose: () => void }) {
  const [copied, setCopied] = useState(false);
  const apiBase = `${window.location.origin}/api`;
  const scriptUrl = `${window.location.origin}/embed/juni-embed.js`;

  const snippet = `<!-- juni Tarifkarten -->
<div data-juni-pricing
     data-juni-key="${embedKey.key}"
     data-juni-api="${apiBase}"
     data-juni-layout="cards"></div>
<script src="${scriptUrl}" defer></script>`;

  return (
    <Modal title={`Einbindung — ${embedKey.name}`} onClose={onClose} wide>
      <div className="px-6 py-5 space-y-4">
        <div>
          <div className="flex items-center justify-between mb-1.5">
            <label className="text-sm font-medium text-slate-700">Plain HTML</label>
            <button
              onClick={() => {
                void navigator.clipboard.writeText(snippet);
                setCopied(true);
                setTimeout(() => setCopied(false), 1800);
              }}
              className="inline-flex items-center gap-1 text-xs text-brand-600 hover:text-brand-800 font-medium"
            >
              {copied ? <Check size={12} /> : <Copy size={12} />} Kopieren
            </button>
          </div>
          <pre className="bg-konsole text-konsole-gruen text-xs p-3 rounded-lg overflow-x-auto">{snippet}</pre>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700">WordPress</label>
          <pre className="bg-konsole text-konsole-gruen text-xs p-3 rounded-lg overflow-x-auto mt-1.5">{`[juni_pricing layout="cards" highlight="pro"]`}</pre>
          <p className="text-xs text-slate-500 mt-1">
            Plugin „juni Pricing Widget" installieren, Key unter Einstellungen → juni Pricing
            eintragen. Alternativ der Block „juni Tarife".
          </p>
        </div>

        <div>
          <label className="text-sm font-medium text-slate-700">TYPO3 / Magento</label>
          <p className="text-xs text-slate-500 mt-1">
            Extension <code className="bg-slate-100 px-1 rounded">juni_pricing</code> bzw. Modul{' '}
            <code className="bg-slate-100 px-1 rounded">Juni_Pricing</code> installieren und den Key
            in der jeweiligen Konfiguration hinterlegen. Danach steht das Inhaltselement bzw. Widget
            „juni Tarife" zur Verfügung.
          </p>
        </div>

        <div className="bg-slate-50 border border-slate-200 rounded-lg p-3">
          <p className="text-xs font-medium text-slate-700 mb-1.5">Optionen</p>
          <div className="text-xs text-slate-600 space-y-0.5 font-mono">
            <div>data-juni-layout: cards | compact | table</div>
            <div>data-juni-product: Produkt-Slug (leer = alle freigegebenen)</div>
            <div>data-juni-highlight: Tarif-Slug als „Empfohlen"</div>
            <div>data-juni-plans: Slug-Liste, kommagetrennt (Reihenfolge zählt)</div>
            <div>data-juni-cycle: monthly | yearly</div>
            <div>data-juni-billing-toggle: false blendet den Umschalter aus</div>
            <div>data-juni-accent: #438369</div>
            <div>data-juni-locale: de | en</div>
          </div>
        </div>

        <div className="bg-amber-50 border border-amber-200 rounded-lg p-3 text-xs text-amber-800">
          Dieser Key funktioniert ausschließlich von: {embedKey.allowedOrigins.join(', ')}. Auf
          anderen Domains liefert die API bewusst 403.
        </div>
      </div>
    </Modal>
  );
}

// ─── Signups ─────────────────────────────────────────────────────────────────

function SignupsModal({ embedKey, onClose }: { embedKey: EmbedKey; onClose: () => void }) {
  const { t } = useTranslation();
  const { data: signups, isLoading } = useQuery({
    queryKey: ['embed-keys', embedKey.id, 'signups'],
    queryFn: () => api.signups(embedKey.id),
  });

  const STATUS: Record<PendingSignup['status'], { label: string; cls: string }> = {
    PENDING: { label: 'Offen', cls: 'bg-amber-50 text-amber-700 border-amber-200' },
    CONVERTED: { label: 'Gekauft', cls: 'bg-green-50 text-green-700 border-green-200' },
    EXPIRED: { label: 'Abgelaufen', cls: 'bg-slate-100 text-slate-500 border-slate-200' },
  };

  return (
    <Modal title={`Anfragen — ${embedKey.name}`} onClose={onClose} wide>
      <div className="px-6 py-5">
        {isLoading ? (
          <div className="space-y-2 animate-pulse">
            {[1, 2, 3].map((i) => <div key={i} className="h-10 bg-slate-100 rounded" />)}
          </div>
        ) : !signups?.length ? (
          <p className="text-sm text-slate-400 italic">{t('embed.noRequestsYet')}</p>
        ) : (
          <div className="max-h-96 overflow-y-auto border border-slate-200 rounded-lg">
            <table className="w-full text-sm">
              <thead className="bg-slate-50 sticky top-0">
                <tr className="text-left text-xs text-slate-500 uppercase tracking-wide">
                  <th className="px-3 py-2 font-medium">Zeitpunkt</th>
                  <th className="px-3 py-2 font-medium">Firma</th>
                  <th className="px-3 py-2 font-medium">E-Mail</th>
                  <th className="px-3 py-2 font-medium">Status</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {signups.map((s) => (
                  <tr key={s.id}>
                    <td className="px-3 py-2 text-xs text-slate-500 whitespace-nowrap">
                      {new Date(s.createdAt).toLocaleString('de-DE')}
                    </td>
                    <td className="px-3 py-2 text-slate-900">{s.companyName}</td>
                    <td className="px-3 py-2 text-slate-600 text-xs">{s.email}</td>
                    <td className="px-3 py-2">
                      <span className={`inline-block text-xs font-medium px-2 py-0.5 rounded-full border ${STATUS[s.status].cls}`}>
                        {STATUS[s.status].label}
                      </span>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>
    </Modal>
  );
}

function Modal({
  title,
  onClose,
  children,
  wide,
}: {
  title: string;
  onClose: () => void;
  children: React.ReactNode;
  wide?: boolean;
}) {
  return (
    <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
      <div className={`bg-surface rounded-xl shadow-xl w-full max-h-screen overflow-y-auto ${wide ? 'max-w-2xl' : 'max-w-md'}`}>
        <div className="px-6 py-5 border-b border-slate-200 flex items-center justify-between sticky top-0 bg-surface">
          <h2 className="text-base font-semibold text-slate-900">{title}</h2>
          <button onClick={onClose} className="text-slate-400 hover:text-slate-600">
            <X size={18} />
          </button>
        </div>
        {children}
      </div>
    </div>
  );
}
